import moment from "moment";
import { Bounce, toast, ToastOptions } from "react-toastify";
import {
  DeliveryMethod,
  OfferTypeEnum,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
} from "../stores/index.d";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { ApiConfig } from "../services/api-config";
import { VehicleType } from "../stores/delivery/index.d";
import { IProductOffer } from "../stores/productOffer";
import React from "react";
import Swal from "sweetalert2";
import { IVoucher, VoucherDiscountType } from "../stores/voucher/index.d";

const alertOpts = {
  theme: "dark",
  position: "top-center",
  autoClose: 500,
  transition: Bounce,
  pauseOnHover: true,
} as ToastOptions;

export const ShowAlert = {
  Default: (content: any) => {
    toast.dismiss();
    toast(content, alertOpts);
  },
  Warning: (content: any) => {
    toast.dismiss();
    toast.warn(content, alertOpts);
  },
  Info: (content: any) => {
    toast.dismiss();
    toast.info(content, alertOpts);
  },
  Success: (content: any) => {
    toast.dismiss();
    toast.success(content, alertOpts);
  },
  Error: (content: any, autoClose?: number) => {
    toast.dismiss();

    const options: ToastOptions = {
      ...alertOpts,
      autoClose: autoClose ?? alertOpts.autoClose,
    };

    toast.error(content, options);
  },
};

export const showConfirmationDialog = async (
  callback: Function,
  title: string = "Are you sure?",
  text: string = "Do you really want to delete this item? This action cannot be undone!",
  buttonText: string = "Yes, delete it!",
  buttonColor: string = "#d33"
) => {
  const result = await Swal.fire({
    title: title,
    text: text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: buttonColor,
    cancelButtonColor: "#6c757d",
    confirmButtonText: buttonText,
    cancelButtonText: "Cancel",
  });

  if (result.isConfirmed) {
    callback();
  }
};

export const currencyFormatter = (price: any) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(price);

export const formatDate = (input: any, format: string = "DD-MM-YYYY HH:mm") =>
  input ? moment(input).format(format) : "NA";

export const currentDateString = (format: string = "YYYY-MM-DD") =>
  moment().format(format);

export const currentMonthStartDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

export const convertToDate = (dateString: string) =>
  moment(dateString).toDate();

export const isDateToday = (date: string) =>
  moment(date).isSame(new Date(), "day");

export const zeroPad = (num: number, size: number) =>
  String(num).padStart(size, "0");

export const getOrderDisplayStatus = (statusId: OrderStatus) => {
  switch (statusId) {
    case OrderStatus.Placed:
      return { text: "New", cssClass: "text-default" };
    case OrderStatus.InProgress:
      return { text: "Preparing", cssClass: "text-primary" };
    case OrderStatus.ReadyForCollection:
      return { text: "Ready For Collection", cssClass: "text-info" };
    case OrderStatus.OutForDelivery:
      return { text: "Out For Delivery", cssClass: "text-info" };
    case OrderStatus.Cancelled:
      return { text: "Cancelled", cssClass: "text-danger" };
    case OrderStatus.Completed:
      return { text: "Completed", cssClass: "text-success" };
    case OrderStatus.RefundInitiated:
      return { text: "Refund Initiated", cssClass: "text-info" };
    case OrderStatus.Refunded:
      return { text: "Refunded", cssClass: "text-success" };
    default:
      return { text: "NA", cssClass: "" };
  }
};

export const getDisplayPaymentStatus = (id: PaymentStatus) => {
  switch (id) {
    case PaymentStatus.Pending:
      return { text: "Pending", cssClass: "text-default" };
    case PaymentStatus.Paid:
      return { text: "Paid", cssClass: "text-success" };
    default:
      return { text: "NA", cssClass: "" };
  }
};

export const isPaymentFailedOrder = (order: {
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
}) =>
  order.paymentMethod === PaymentMethod.Card &&
  order.paymentStatus === PaymentStatus.Pending;

const PAYMENT_CONFIRMATION_GRACE_MS = 2 * 60 * 1000; // 2 minutes

const isPaymentAwaitingConfirmation = (order: {
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  orderDate?: string;
}) =>
  isPaymentFailedOrder(order) &&
  !!order.orderDate &&
  Date.now() - new Date(order.orderDate).getTime() < PAYMENT_CONFIRMATION_GRACE_MS;

export const getOrderDisplayStatusForOrder = (order: {
  orderStatus: OrderStatus;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  orderDate?: string;
}) => {
  if (isPaymentFailedOrder(order)) {
    return isPaymentAwaitingConfirmation(order)
      ? { text: "Awaiting confirmation", cssClass: "text-warning" }
      : { text: "Payment Failed", cssClass: "text-danger" };
  }

  return getOrderDisplayStatus(order.orderStatus);
};

export const getDisplayPaymentStatusForOrder = (order: {
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  orderDate?: string;
}) => {
  if (isPaymentFailedOrder(order)) {
    return isPaymentAwaitingConfirmation(order)
      ? { text: "Awaiting confirmation", cssClass: "text-warning" }
      : { text: "Payment Failed", cssClass: "text-danger" };
  }

  return getDisplayPaymentStatus(order.paymentStatus);
};

export const getDisplayDeliveryType = (id: DeliveryMethod) => {
  switch (id) {
    case DeliveryMethod.Collection:
      return { text: "Collection", cssClass: "text-primary" };
    case DeliveryMethod.Delivery:
      return { text: "Delivery", cssClass: "text-success" };
    case DeliveryMethod.DineIn:
      return { text: "Dine-In", cssClass: "text-dark" };
    default:
      return { text: "NA", cssClass: "" };
  }
};

export const getDisplayVehicleType = (val: VehicleType) => {
  switch (val) {
    case VehicleType.Car:
      return { text: "Car", cssClass: "fa fa-car fs-4 text-warning" };
    case VehicleType.Bike:
      return { text: "Bike", cssClass: "fa fa-motorcycle fs-4 text-primary" };
    case VehicleType.Bicycle:
      return { text: "Bicycle", cssClass: "fa fa-bicycle fs-4 text-danger" };
    default:
      return { text: "NA", cssClass: "" };
  }
};

export const creatHubConnection = (
  hubUrl: string,
  hubMethod: string,
  callback: Function
) => {
  const connection = new HubConnectionBuilder()
    .withUrl(ApiConfig.ROOT_URL + hubUrl, {
      withCredentials: false,
    })
    .withAutomaticReconnect()
    .build();

  const connect = async () => {
    connection.on(hubMethod, (user, data) => {
      callback(data);
    });

    try {
      await connection.start();
    } catch (ex) {
      console.log("Hub connection failed for " + hubMethod);
    }
  };

  connect();

  return connection;
};

export const getAsapTime = (asapTime: number) => {
  return moment(new Date()).add(asapTime, "minutes").format("HH:mm");
};

export const getStartMins = (currentMin: number) => {
  if (currentMin < 15) return 15;
  if (currentMin > 15 && currentMin < 30) return 30;
  if (currentMin > 30 && currentMin < 45) return 45;
  if (currentMin > 45 && currentMin < 60) return 60;
  return 0;
};

export const timeToDate = (time: string) => {
  if (!time || !time.toString().includes(":")) return null;

  let h = time.split(":");
  return moment(currentDateString())
    .add(h[0], "hours")
    .add(h[1], "minutes")
    .toDate();
};

export const getHoursByInterval = (
  start: Date,
  end: Date,
  interval: number,
  asapTime: number,
  shoAsapTime: boolean
) => {
  let hours = [];

  if (shoAsapTime) {
    hours.push({
      value: getAsapTime(asapTime),
      text: `A.S.A.P(est. ${asapTime} mins)`,
    });
    start = moment(start).add(asapTime, "minutes").toDate();
  }

  const cM = start.getMinutes();
  const startMin = getStartMins(cM);
  start = moment(moment(start).format("YYYY-MM-DD"))
    .add(start.getHours(), "hours")
    .add(startMin, "minutes")
    .toDate();

  do {
    start = moment(start).add(interval, "minutes").toDate();
    var hm = moment(start).format("HH:mm");
    hours.push({ value: hm, text: hm });
  } while (start < end);

  return hours;
};

export const getShopWorkingHours = (
  shopStartTime: string,
  shopEndTime: string,
  shopOpenHoursInterval: number,
  selectedDate: string,
  asapTime: number
) => {
  if (shopOpenHoursInterval === 0)
    return [{ value: "", text: "Not available" }];

  const startTime = timeToDate(shopStartTime);
  const endTime = timeToDate(shopEndTime);
  if (!startTime || !endTime) return [];

  const isToday = moment(selectedDate).isSame(new Date(), "day");
  if (isToday) {
    const today = moment(new Date()).toDate();
    const isTomorrow = moment(today).isAfter(new Date(), "day");
    if (isTomorrow) return [{ value: "", text: "Not available" }];

    const isCurrentHour = today >= startTime;
    return getHoursByInterval(
      isCurrentHour ? today : startTime,
      endTime,
      shopOpenHoursInterval,
      asapTime,
      true
    );
  }

  return getHoursByInterval(
    startTime,
    endTime,
    shopOpenHoursInterval,
    asapTime,
    true
  );
};

export const getShopFutureDates = (
  onOfDays: number = 7,
  allowFutureDateOrders: boolean,
  weeklyShopClosureDays: string
) => {
  let today = moment();
  let days = [{ value: today.format("YYYY-MM-DD"), text: "Today" }];
  if (!allowFutureDateOrders) return days;
  for (let day = 1; day < onOfDays; day++) {
    today.add(1, "day");
    const dayNumber = today.toDate().getDay().toString();
    const isWeeklyClosureDay = weeklyShopClosureDays?.includes(dayNumber);
    if (isWeeklyClosureDay) continue;
    days.push({
      value: today.format("YYYY-MM-DD"),
      text: today.format("dddd, DD MMM yy"),
    });
  }
  return days;
};

export function addSpaceBeforeCapital(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function getEnumOptions<T extends object>(
  enumObj: T,
  inculeAllOption: boolean = false
): { label: string; value: string }[] {
  let opts = Object.keys(enumObj)
    .filter((key) => isNaN(Number(key))) // Keep only the string keys
    .map((key) => ({
      label: addSpaceBeforeCapital(key),
      value: enumObj[key as keyof T] as string,
    }));

  if (inculeAllOption) opts.unshift({ label: "All", value: "0" });

  return opts;
}

type ObjectType = Record<string, any>;

export function compareObjectsExcluding<T extends ObjectType>(
  obj1: T,
  obj2: T,
  excludeKeys: string[]
): boolean {
  const filteredObj1 = Object.fromEntries(
    Object.entries(obj1).filter(([key]) => !excludeKeys.includes(key))
  );

  const filteredObj2 = Object.fromEntries(
    Object.entries(obj2).filter(([key]) => !excludeKeys.includes(key))
  );

  return JSON.stringify(filteredObj1) === JSON.stringify(filteredObj2);
}

export function getOfferDescription(
  offer: IProductOffer,
  forTitle: boolean = false
): JSX.Element {
  if (!offer) return React.createElement("div");
  switch (offer.offerType) {
    case OfferTypeEnum.Buy1Get1:
      return React.createElement("div", null, "Buy 1 Get 1 free");
    case OfferTypeEnum.Discount:
      return React.createElement(
        "div",
        null,
        `Get ${offer.discountPercentage}% discount`
      );
    case OfferTypeEnum.FreeProduct:
      const span = React.createElement(
        "span",
        { className: `badge ${forTitle ? "bg-orange" : "bg-success"} fs-6` },
        `${offer.freeProduct?.productName}`
      );
      return React.createElement("div", null, "Get free product ", span);
    case OfferTypeEnum.MultiBuys:
      let div = React.createElement("div");
      return React.createElement(
        "div",
        null,
        `Buy ${offer.multiBuyQuantity}, Get ${offer.multiBuyDiscountPercentage}% discount`
      );
    default:
      return React.createElement("div");
  }
}

export function getOfferAppliedDesc(
  offer: IProductOffer,
  itemQty: number
): JSX.Element {
  if (!offer) return React.createElement("div");
  switch (offer.offerType) {
    case OfferTypeEnum.Discount:
      return React.createElement(
        "div",
        { className: `small` },
        `${offer.discountPercentage}% discount applied`
      );
    case OfferTypeEnum.Buy1Get1:
      return React.createElement(
        "div",
        { className: `small` },
        "Buy 1 Get 1 free"
      );
    case OfferTypeEnum.FreeProduct:
      const span = React.createElement(
        "i",
        { className: `fw-bold` },
        `${offer.freeProduct?.productName}`
      );
      return React.createElement(
        "div",
        { className: `small` },
        `Free item `,
        span
      );
    case OfferTypeEnum.MultiBuys:
      return React.createElement(
        "div",
        null,
        itemQty == offer.multiBuyQuantity
          ? `${offer.multiBuyDiscountPercentage}% discount applied`
          : ""
      );
    default:
      return React.createElement("div");
  }
}

export function getDiscountedValue(
  orderValue: number,
  discountType: VoucherDiscountType,
  discountValue: number
): [number, number] {
  if (orderValue <= 0 || discountValue <= 0) return [orderValue, 0];

  let discountPrice = 0;

  if (discountType === VoucherDiscountType.Percentage) {
    discountPrice = (orderValue * discountValue) / 100;
  } else if (discountType === VoucherDiscountType.Fixed) {
    discountPrice = discountValue;
  }

  const finalPrice = orderValue - discountPrice;

  // Ensure price doesn’t go below zero
  return [Math.max(0, parseFloat(finalPrice.toFixed(2))), discountPrice];
}

export const getDiscountDetail = (voucher: IVoucher): string => {
  switch (voucher.discountType) {
    case VoucherDiscountType.FreeDelivery:
      return "Free Delivery";
    case VoucherDiscountType.Percentage:
      return `${voucher.discountValue}% off`;
    default:
      return `Flat £${voucher.discountValue} off`;
  }
};

export const generateVoucherCode = (length: number = 10) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const array = new Uint32Array(length);
  window.crypto.getRandomValues(array);

  return Array.from(array, (x) => chars[x % chars.length]).join("");
};
